import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StrategyQuestion {
  id: string;
  section: string;
  questionKey: string;
  label: string;
  description?: string | null;
  type:
    | 'text'
    | 'textarea'
    | 'select'
    | 'multiselect'
    | 'checkboxes'
    | 'cards'
    | 'multitext'
    | 'ranked';
  options?: string[] | null;
  otherLimit: number;
  order: number;
  required: boolean;
}

export interface StrategyResponse {
  id: string;
  locationId: string;
  questionId: string;
  response: unknown;
  updatedAt: string;
  question: { questionKey: string; section: string };
}

export interface SectionCompletion {
  section: string;
  total: number;
  answered: number;
  requiredTotal: number;
  requiredAnswered: number;
  complete: boolean;
}

export interface StrategyCompletion {
  percent: number;
  bySection: SectionCompletion[];
}

export interface StrategyItem {
  title: string;
  type: 'OPPORTUNITY' | 'GAP';
  target_segment: string;
  rationale: string;
  actions: string[];
  prerequisites: string[];
  benchmark_key: string;
  recommended_monthly_budget: number;
  /** One-time setup cost; absent on strategies generated before it existed. */
  one_time_cost?: number;
  primary_channels: string[];
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_rationale: string;
  revenue_impact?: string;
  patients_per_year?: string;
  roi_range?: string;
  revenue_low?: number;
  revenue_high?: number;
  patients_low?: number;
  patients_high?: number;
}

export interface InsightsSummary {
  totalAnnualBudget: string;
  totalRevenueImpact: string;
  totalPatientsRange: string;
}

export interface InsightsResult {
  /** Strategy version this insights snapshot belongs to. */
  version: number;
  strategies: StrategyItem[];
  summary: InsightsSummary;
  budgetAllocationRationale: string;
  enrichmentLevel: string;
  generatedAt: string;
  approvedIndexes: number[];
  practiceRevenue: number;
  /** True when an upstream input was modified after `generatedAt`. */
  stale: boolean;
  /** What changed since these insights were generated (empty for older versions). */
  inputChanges?: InputChange[];
  /** For a draft: its input changes relative to the live version. */
  changesSinceLive?: { version: number; changes: InputChange[] } | null;
}

export interface InputChange {
  kind: 'answer' | 'radius' | 'competitors' | 'upload' | 'profile';
  label: string;
  detail: string;
}

export type ArchetypeName = 'Caregiver' | 'Sage' | 'Everyman' | 'Hero';

export interface AakerDimension {
  dimension: string;
  level: 'High' | 'Moderate' | 'Low';
}

export interface BrandProfileData {
  structureScore: number;
  masteryScore: number;
  primary: ArchetypeName;
  secondary: ArchetypeName;
  label: string;
  answeredCount: number;
  toneOfVoice: string;
  copyLean: string;
  aaker: AakerDimension[];
}

export interface BrandKitPaletteColor {
  hex: string;
  role: 'primary' | 'light' | 'accent' | 'neutral';
}

export interface BrandKitOption {
  id: 'warm' | 'trusted' | 'clean';
  name: string;
  tagline: string;
  vibe: string;
  typeLabel: string;
  palette: BrandKitPaletteColor[];
  previewBg: string;
  previewFg: string;
  pros: string[];
  cons: string[];
}

export interface BrandKitData {
  direction: string | null;
  approvedAt: string | null;
  options: BrandKitOption[] | null;
  optionsGeneratedAt: string | null;
}

export interface BrandPlatformPersonalityNote {
  dimension: string;
  note: string;
}

export interface BrandPlatformValue {
  name: string;
  meaning: string;
}

export interface BrandPlatformVoice {
  traits: string[];
  example: string;
}

export interface BrandPlatformContent {
  essence: string;
  positioning: string;
  enemy: string;
  personality_notes: BrandPlatformPersonalityNote[];
  values: BrandPlatformValue[];
  voice: BrandPlatformVoice;
}

export interface BrandPlatformData {
  content: BrandPlatformContent | null;
  generatedAt: string | null;
}

export type CallState =
  | 'scheduling'
  | 'dialing'
  | 'in_progress'
  // The call ended; its answers are being processed.
  | 'processing'
  | 'ended'
  | 'no_answer'
  | 'voicemail'
  | 'wrong_party'
  | 'cancelled'
  | 'failed'
  | 'captured_complete'
  | 'captured_partial'
  | 'not_captured'
  | 'confirmed';

export type FieldStatusValue = 'captured' | 'unclear' | 'not_reached';

export type CaptureLevel = 'complete' | 'partial' | 'none';

export interface VapiCall {
  id: string;
  vapiCallId: string;
  locationId: string;
  status: 'scheduled' | 'complete' | 'incomplete' | 'reschedule' | 'declined_recording';
  callState: CallState | null;
  endedReason: string | null;
  captureLevel: CaptureLevel | null;
  fieldStatus: Record<string, FieldStatusValue> | null;
  confirmedAt: string | null;
  attempts: number;
  lastStatusAt: string | null;
  scheduledAt: string | null;
  strategyConfidence: string | null;
  contactRole: string | null;
  missingFields: string[];
  rescheduleNote: string | null;
  consentToRecord: boolean;
  processedAt: string;
  location: { name: string };
  /** The call's answers were stored, so it can be re-processed. */
  hasStoredAnswers: boolean;
}

export interface VapiSession {
  id: string;
  vapiCallId: string;
  callState: CallState | null;
  captureLevel: CaptureLevel | null;
  fieldStatus: Record<string, FieldStatusValue> | null;
  confirmedAt: string | null;
  lastStatusAt: string | null;
  scheduledAt: string | null;
  phoneNumber: string | null;
  attempts: number;
}

export interface VapiTokenSession {
  id: string;
  locationId: string;
  callState: CallState | null;
  captureLevel: CaptureLevel | null;
  fieldStatus: Record<string, FieldStatusValue> | null;
  confirmedAt: string | null;
  confirmTokenExpiresAt: string | null;
  tokenExpired: boolean;
}

export interface OverviewStats {
  marketPopulation: number | null;
  zipCount: number;
  totalPatients: number | null;
  marketSharePercent: number | null;
  competitorCount: number;
  competitorUpdatedAt: string | null;
  censusUpdatedAt: string | null;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const strategyApi = {
  getQuestions: (locationId: string) =>
    apiClient
      .get<ApiResponse<StrategyQuestion[]>>('/strategy/questions', { params: { locationId } })
      .then((r) => r.data),

  getResponses: (locationId: string) =>
    apiClient
      .get<ApiResponse<StrategyResponse[]>>('/strategy/responses', { params: { locationId } })
      .then((r) => r.data),

  saveResponses: (locationId: string, updates: Array<{ questionId: string; response: unknown }>) =>
    apiClient
      .put<ApiResponse<StrategyResponse[]>>('/strategy/responses', { locationId, updates })
      .then((r) => r.data),

  getCompletion: (locationId: string) =>
    apiClient
      .get<ApiResponse<StrategyCompletion>>('/strategy/completion', { params: { locationId } })
      .then((r) => r.data),

  getInsights: (locationId: string, version?: number) =>
    apiClient
      .get<ApiResponse<InsightsResult | null>>('/strategy/insights', {
        params: { locationId, ...(version !== undefined ? { version } : {}) },
      })
      .then((r) => r.data),

  generateInsights: (locationId: string, version?: number) =>
    apiClient
      // Queued: returns the version being generated and the strategy_insights
      // workflow to watch; the insights arrive when that workflow completes.
      .post<ApiResponse<{ version: number; workflowId: string | null }>>(
        '/strategy/insights/generate',
        { locationId, ...(version !== undefined ? { version } : {}) },
      )
      .then((r) => r.data),

  getOverview: (locationId: string) =>
    apiClient
      .get<ApiResponse<OverviewStats>>('/strategy/overview', { params: { locationId } })
      .then((r) => r.data),

  getBrandProfile: (locationId: string) =>
    apiClient
      .get<ApiResponse<BrandProfileData | null>>('/strategy/brand-profile', {
        params: { locationId },
      })
      .then((r) => r.data),

  getBrandKit: (locationId: string) =>
    apiClient
      .get<ApiResponse<BrandKitData>>('/strategy/brand-kit', { params: { locationId } })
      .then((r) => r.data),

  saveBrandKit: (locationId: string, direction: string) =>
    apiClient
      .put<ApiResponse<BrandKitData>>('/strategy/brand-kit', { locationId, direction })
      .then((r) => r.data),

  clearBrandKit: (locationId: string) =>
    apiClient
      .delete<ApiResponse<null>>('/strategy/brand-kit', { params: { locationId } })
      .then((r) => r.data),

  generateBrandKit: (locationId: string) =>
    apiClient
      .post<ApiResponse<BrandKitOption[]>>('/strategy/brand-kit/generate', { locationId })
      .then((r) => r.data),

  getBrandPlatform: (locationId: string) =>
    apiClient
      .get<ApiResponse<BrandPlatformData>>('/strategy/brand-platform', { params: { locationId } })
      .then((r) => r.data),

  generateBrandPlatform: (locationId: string) =>
    apiClient
      .post<ApiResponse<BrandPlatformContent>>('/strategy/brand-platform/generate', { locationId })
      .then((r) => r.data),

  initiateVapiCall: (locationId: string, phoneNumber: string, scheduledAt?: string) =>
    apiClient
      .post<ApiResponse<{ callId: string }>>('/strategy/vapi/call', {
        locationId,
        phoneNumber,
        scheduledAt,
      })
      .then((r) => r.data),

  getVapiCalls: (practiceId: string) =>
    apiClient
      .get<ApiResponse<VapiCall[]>>('/strategy/vapi/calls', { params: { practiceId } })
      .then((r) => r.data),

  reprocessVapiCall: (vapiCallId: string) =>
    apiClient
      .post<
        ApiResponse<{ reprocessed: boolean; vapiCallId: string }>
      >(`/strategy/vapi/reprocess/${vapiCallId}`)
      .then((r) => r.data),

  cancelVapiSession: (locationId: string) =>
    apiClient
      .delete<ApiResponse<{ cancelled: boolean }>>('/strategy/vapi/session', {
        params: { locationId },
      })
      .then((r) => r.data),

  getVapiSession: (locationId: string) =>
    apiClient
      .get<ApiResponse<VapiSession | null>>('/strategy/vapi/session', { params: { locationId } })
      .then((r) => r.data),

  confirmVapiSession: (locationId: string, sessionId?: string) =>
    apiClient
      .post<ApiResponse<{ confirmed: boolean }>>('/strategy/vapi/confirm', {
        locationId,
        ...(sessionId ? { sessionId } : {}),
      })
      .then((r) => r.data),

  resendConfirmEmail: (locationId: string) =>
    apiClient
      .post<ApiResponse<{ sent: boolean }>>('/strategy/vapi/resend-confirm', { locationId })
      .then((r) => r.data),

  getSessionByConfirmToken: (token: string) =>
    apiClient
      .get<ApiResponse<VapiTokenSession>>('/strategy/vapi/confirm-token', { params: { token } })
      .then((r) => r.data),
};
